import { TrendingUp } from "lucide-react";
import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import useMixpanelEvent from "@/hooks/useMixpanelEvent";
import { useUser } from "@/context/UserContext";
import { getAgentProfileProperties } from "@/services/PropertyService";
import { PropertyCard } from "./PropertyCard";

function puzzleUrlFromProperty(property: any): string | null {
    if (!property) return null;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    if (property.shortLink) {
        return `${origin}/puzzle/${property.shortLink}`;
    }
    if (property.longLink) {
        // Match routing in handleNavigateToProperty: `/puzzle${property.longLink}`
        return `${origin}/puzzle${property.longLink}`;
    }
    return null;
}

/**
 * Normalize a play date value coming from the API to a safe ISO string.
 * Returns an empty string when the input is missing or invalid.
 */
function normalizePlayDate(playDate: unknown): string {
    if (!playDate) {
        return "";
    }
    if (playDate instanceof Date) {
        return isNaN(playDate.getTime()) ? "" : playDate.toISOString();
    }
    if (typeof playDate === "string") {
        const parsedDate = new Date(playDate);
        return isNaN(parsedDate.getTime()) ? "" : parsedDate.toISOString();
    }
    return "";
}

function EmptyState({
    icon,
    title,
    description,
}: {
    icon: React.ReactNode;
    title: string;
    description: string | React.ReactNode;
}) {
    return (
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-12 tw-text-center md:tw-py-16">
            <div className="tw-text-gray-300 tw-mb-3 md:tw-mb-4">{icon}</div>
            <h3 className="tw-text-gray-900 tw-text-lg tw-mb-2 tw-font-light md:tw-text-xl">{title}</h3>
            <p className="tw-text-gray-600 tw-max-w-md tw-px-4 tw-text-sm tw-font-light">{description}</p>
        </div>
    );
}

function TabPlaceholder({ text }: { text: string }) {
    return <div className="tw-text-center tw-text-sm md:tw-text-base tw-text-[var(--nobel-gray)] tw-py-8">{text}</div>;
}

interface MyListingsTabProps {
    nickname?: string;
}

export function MyListingsTab({ nickname }: MyListingsTabProps) {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth0();
    const { userState } = useUser();
    const { sendEvent: sendMixpanelEvent } = useMixpanelEvent();
    const [showScoreIndex, setShowScoreIndex] = useState<number | null>(null);
    const [page, setPage] = useState(1);
    const [allProperties, setAllProperties] = useState<any[]>([]);
    const [lastAppendedPage, setLastAppendedPage] = useState(0);

    const { data, isLoading, error, isFetching } = useQuery({
        queryKey: ["agent-profile-properties", nickname, page, userState?.id],
        queryFn: () => getAgentProfileProperties(nickname!, { page, limit: 10, requestingUserId: userState?.id }),
        enabled: !!nickname,
        refetchOnWindowFocus: false,
    });

    React.useEffect(() => {
        setPage(1);
        setAllProperties([]);
        setShowScoreIndex(null);
        setLastAppendedPage(0);
    }, [nickname,userState?.id]);

    React.useEffect(() => {
        if (data?.data && page > lastAppendedPage) {
            if (page === 1) {
                setAllProperties(data.data);
            } else {
                setAllProperties((prev) => [...prev, ...data.data]);
            }
            setLastAppendedPage(page);
        }
    }, [data, page, lastAppendedPage]);

    const handleNavigateToProperty = (property: any) => {
        if (property.shortLink) {
            navigate(`/puzzle/${property.shortLink}`);
        } else if (property.longLink) {
            navigate(`/puzzle${property.longLink}`);
        } else {
            return null;
        }
    };

    const handleToggleScore = (index: number) => {
        const isClosing = showScoreIndex === index;
        const toggleTo = isClosing ? "back_to_listing" : "stats";
        const property = allProperties[index];
        const profileNickname = (nickname ?? "").trim();
        const currentNick = userState?.nickname?.trim();
        const profileViewed =
            currentNick && profileNickname && currentNick.toLowerCase() === profileNickname.toLowerCase()
                ? "own"
                : "other";

        sendMixpanelEvent("Profile Card Flip", {
            isAuthenticated,
            pageUrl: window.location.href,
            email: userState?.emailAddress ?? null,
            nickname: userState?.nickname ?? null,
            profileViewed,
            puzzleUrl: puzzleUrlFromProperty(property),
            toggleTo,
        });

        setShowScoreIndex(showScoreIndex === index ? null : index);
    };

    const handleLoadMore = () => {
        if (data && "hasNextPage" in data && data.hasNextPage) {
            setPage((prev) => prev + 1);
        }
    };

    if (isLoading && page === 1) {
        return (
            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3 tw-mx-auto">
                {Array.from({ length: 6 }).map((_, index) => (
                    <div key={index} className="tw-bg-gray-800 tw-rounded-2xl tw-h-[280px] tw-animate-pulse">
                        <div className="tw-w-full tw-h-full tw-bg-gradient-to-t tw-from-gray-900 tw-to-gray-800 tw-rounded-2xl tw-relative">
                            <div className="tw-absolute tw-bottom-4 tw-left-4 tw-right-4">
                                <div className="tw-h-4 tw-bg-gray-700 tw-rounded tw-mb-2"></div>
                                <div className="tw-h-3 tw-bg-gray-700 tw-rounded tw-w-3/4"></div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (error) {
        return (
            <EmptyState
                icon={<TrendingUp className="tw-w-10 tw-h-10" />}
                title="Failed to load properties"
                description="Reload the page and try again"
            />
        );
    }

    return (
        <>
            {allProperties.length > 0 ? (
                <>
                    {/* Consistent grid layout - cards always same size, left-justified */}
                    <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3 tw-w-full">
                        {allProperties.map((property: any, index: number) => {
                            const totalGuessers = property.communityStats?.userGuessCount ?? property.userGuessCount ?? 0;

                            return (
                                <PropertyCard
                                    key={property.playablePropertyId}
                                    image={property.propertyImage || "/placeholder-property.jpg"}
                                    address={property.address}
                                    city={property.city || ""}
                                    state={property.state || ""}
                                    guessersCount={totalGuessers}
                                    datePlayable={normalizePlayDate(property.playDate)}
                                    beds={property.beds}
                                    baths={parseFloat(property.baths)}
                                    sqft={property.sqft}
                                    propertyStats={{
                                        avgFirstGuess: property.communityStats?.avgFirstGuess || 0,
                                        avgScore: property.communityStats?.avgScore || 0,
                                        highestGuess: property.communityStats?.highestGuess || 0,
                                        lowestGuess: property.communityStats?.lowestGuess || 0,
                                        totalGuessers,
                                    }}
                                    viewerHasPlayed={property.viewerHasPlayed || false}
                                    onClick={() => handleNavigateToProperty(property)}
                                    index={index}
                                    showScoreIndex={showScoreIndex}
                                    onToggleScore={handleToggleScore}
                                />
                            );
                        })}
                    </div>
                    {data && "hasNextPage" in data && data.hasNextPage && (
                        <div className="tw-flex tw-justify-center tw-mt-6">
                            <button
                                onClick={handleLoadMore}
                                disabled={isFetching}
                                className="tw-bg-purple-600 hover:tw-bg-purple-700 tw-text-white tw-px-6 tw-py-2 tw-rounded-lg tw-font-medium tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                            >
                                {isFetching ? "Loading..." : "Load More"}
                            </button>
                        </div>
                    )}
                </>
            ) : (
                <EmptyState
                    icon={<TrendingUp className="tw-w-10 tw-h-10" />}
                    title="There's nothing here…yet."
                    description={
                        <>
                            <a
                                href="https://www.priceme.game/contact"
                                className="tw-text-purple-600 hover:tw-underline"
                            >
                                Add your first listing
                            </a>
                            .
                        </>
                    }
                />
            )}
        </>
    );
}

export function CommentsTab() {
    return <TabPlaceholder text="Comments will appear here." />;
}

export function SavedTab() {
    return <TabPlaceholder text="Saved listings will appear here." />;
}

export function AboutTab() {
    return <TabPlaceholder text="About information will appear here." />;
}
